import CopyIconButton from "../../components/CopyIconButton";
import Layout from "../../components/Layout";
import Table from "../../components/Table";
import Link from "next/link";
import { Link as LinkComponent } from "ui";
import { useState } from "react";
import { PlanGroupDrawerForm } from "../../features/subscriptions/components/PlanGroupDrawerForm";
import { SubscriptionNavigationTabs } from "../../components/NavigationTabs/SubscriptionNavigationTabs";

type SubscriptionEmbedDetails = {
  id: string;
  name: string;
  account: {
    id: string;
    name: string;
  };
  currency: string;
  plans: {
    id: string;
    name: string;
  }[];
  webhook_url: string;
  embed_url: string;
};

export default function PlanGroupsPage() {
  const [planGroupDrawer, setPlanGroupDrawer] = useState<{
    mode: "add" | "edit";
    isOpen: boolean;
    planGroupInitialValues: any;
  }>({
    mode: "add",
    isOpen: false,
    planGroupInitialValues: null,
  });

  return (
    <Layout metaTitle="Plan Groups">
      <Table
        url="/d/subscriptions/embeds/"
        tableName="Plan Group"
        tableDescription="Plan Groups are used to group multiple plans together for subscription management."
        customPreview={customPreview}
        onAddItemClick={() =>
          setPlanGroupDrawer({
            mode: "add",
            isOpen: true,
            planGroupInitialValues: null,
          })
        }
        onEditItemClick={(_id, _row, preview) => {
          setPlanGroupDrawer({
            mode: "edit",
            isOpen: true,
            planGroupInitialValues: preview,
          });
        }}
        navigationTabs={<SubscriptionNavigationTabs activeTab="planGroups" />}
      />

      <PlanGroupDrawerForm
        mode={planGroupDrawer.mode}
        isOpen={planGroupDrawer.isOpen}
        planGroupInitialValues={planGroupDrawer.planGroupInitialValues}
        onClose={() =>
          setPlanGroupDrawer((prev) => ({ ...prev, isOpen: false }))
        }
      />
    </Layout>
  );
}

PlanGroupsPage.parentPath = "/plan-groups";

const customPreview = {
  Content: ({ preview }: { preview: SubscriptionEmbedDetails }) => {
    return (
      <div className="flex flex-col space-y-6">
        <div>
          <p className="mb-1 text-xs font-medium text-foundations-subtle">
            Name
          </p>

          <p className="truncate text-sm font-medium text-foundations-boldest">
            {preview.name}
          </p>
        </div>
        <div>
          <p className="mb-1 text-xs font-medium text-foundations-subtle">
            Account
          </p>

          <Link
            href={`/settings/accounts/${preview.account.id}`}
            className="truncate text-sm font-medium text-foundations-boldest hover:text-gold-400 "
          >
            {preview.account.name}
          </Link>
        </div>
        <div>
          <p className="mb-1 text-xs font-medium text-foundations-subtle">
            Currency
          </p>

          <p className="truncate text-sm font-medium text-foundations-boldest">
            {preview.currency}
          </p>
        </div>
        <div>
          <p className="mb-1 text-xs font-medium text-foundations-subtle">
            Plans
          </p>

          {preview.plans.map((plan, index) => (
            <>
              <Link
                href={{
                  pathname: `/plans/${plan.id}`,
                }}
                className="break-words text-sm font-medium text-foundations-boldest hover:text-gold-400"
              >
                {plan.name}
              </Link>

              {index < preview.plans.length - 1 && ", "}
            </>
          ))}
        </div>
        <div>
          <p className="flex justify-between text-xs font-medium capitalize text-foundations-subtle">
            <span>Webhook Url</span>
            <CopyIconButton text={preview.webhook_url} />
          </p>

          <LinkComponent
            href={preview.webhook_url}
            target="_blank"
            truncate
            rel="noreferrer"
          >
            {preview.webhook_url}
          </LinkComponent>
        </div>
        <div>
          <p className="flex justify-between text-xs font-medium capitalize text-foundations-subtle">
            <span>Embed Url</span>
            <CopyIconButton text={preview.embed_url} />
          </p>

          <LinkComponent href={preview.embed_url} target="_blank" truncate>
            {preview.embed_url}
          </LinkComponent>
        </div>
      </div>
    );
  },
};
